import React, { ChangeEvent, useState, type JSX } from 'react';
import styled from 'styled-components';
import Select from 'components/UI/Select';
import Input from 'components/UI/Input';
import FlexboxContainer from 'components/UI/FlexboxContainer';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { FontWeight } from 'lib/styles';
import { Text } from 'styles/themeColors';
import { Space } from 'styles/themeSpaces';
import * as Cons from 'data/Consignment';
import RentalDates from './components/RentalDates';
import { useReadPermission } from 'hooks/useUserRoles';
import { PermissionCode } from 'data/UserPermissions';
import { ReadOnlyWrapper } from 'components/common/ReadOnlyWrapper';
import { DateTime } from 'luxon';

const LabelText = styled.span`
  display: block;
  font-weight: ${FontWeight.medium};
  text-align: left;
  margin-bottom: 5px;
  color: ${Text.primary};
  font-size: 15px;
`;

interface Props {
  shippingCarrierValue: string;
  shippingCarriers: any[];
  onChange: (event: any) => void;
  trackingId: string;
  onTrackingIdChange: (e: ChangeEvent<HTMLInputElement>) => void;
  deliveryDate?: string | null;
  onDeliveryDateChange: (value: any) => void;
  button?: JSX.Element;
  isRental?: boolean;
  consignmentData?: Cons.ConsignmentDetails,
  setShowModal?: (show: boolean) => void;
};

const ShippingDetails = ({
  shippingCarrierValue,
  shippingCarriers,
  onChange,
  trackingId,
  onTrackingIdChange,
  deliveryDate,
  onDeliveryDateChange,
  button,
  isRental,
  consignmentData,
  setShowModal
}: Props) => {

  const { readOnly } = useReadPermission(PermissionCode.PREPARING_SHIPPING_DETAILS);
  const [openDatePicker, setOpenDatePicker] = useState(false);

  return (
    <>
      <FlexboxContainer flexDirection="row" width="100%" justifyContent="space-between">
        <FlexboxContainer flexDirection="row" alignItems="center" padding="5px 0 0 0" >
          <FlexboxContainer width='200px' margin={`0 ${Space.spHalf} 0 0`}>
            <Select
              noBoxShadow
              options={shippingCarriers}
              value={shippingCarrierValue}
              defaultValue="Select"
              onChange={onChange}
              labelPosition='top'
              label="Carrier"
              width="100%"
              readOnly={readOnly}
            />
          </FlexboxContainer>
          <Input
            id="tracking-id"
            labelName="Tracking #"
            placeholder="Enter tracking number"
            value={trackingId}
            onChange={onTrackingIdChange}
            width="224px"
            maxLength='25'
            readOnly={readOnly}
          />
          <FlexboxContainer flexDirection="column" width='150px' margin='0 0 0 4px'>
            <LabelText>Est. ship date</LabelText>
            <ReadOnlyWrapper readOnly={readOnly}>
              <DatePicker
                value={deliveryDate ? DateTime.fromISO(deliveryDate) : null}
                onChange={(value: DateTime | null) => {
                  if (value) {
                    onDeliveryDateChange(value.toISO() || '');
                  }
                }}
                open={openDatePicker}
                onOpen={() => setOpenDatePicker(true)}
                onClose={() => setOpenDatePicker(false)}
                format="yyyy MMM dd"
                slotProps={{
                  textField: { size: 'small', 
                    onClick: () => setOpenDatePicker(true),
                    InputProps: {
                    readOnly: true,
                    sx: { cursor: 'pointer' },
                    }}
                }}
                disabled={readOnly}
              />
            </ReadOnlyWrapper>
          </FlexboxContainer>
        </FlexboxContainer>
        <FlexboxContainer margin="30px 20px 0 4px" justifyContent="flex-end" alignItems="flex-end">
          {button}
        </FlexboxContainer>
      </FlexboxContainer>
      {isRental ? <RentalDates datesFormat='yyyy MMM dd' consignmentData={consignmentData} isRental={isRental} /> : ''}
    </>
  );
};

export default React.memo(ShippingDetails);
